import React, { useMemo } from 'react';
import { Youtube, ExternalLink } from 'lucide-react';

/**
 * Extracts YouTube video ID from various URL formats
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/
 */
const extractYouTubeId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

/**
 * Checks if a string contains a YouTube URL
 */
export const containsYouTubeUrl = (text) => {
  if (!text) return null;
  const urlPattern = /(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|shorts\/)?([a-zA-Z0-9_-]{11})/i;
  const match = text.match(urlPattern);
  return match ? match[0] : null;
};

/**
 * YouTube Embed Component
 */
const YouTubeEmbed = ({ url, className = '' }) => {
  const videoId = useMemo(() => extractYouTubeId(url), [url]);
  
  if (!videoId) {
    return null;
  }
  
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
  
  return (
    <div className={`relative rounded-xl overflow-hidden bg-black ${className}`} data-testid="youtube-embed">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedUrl}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute top-0 left-0 w-full h-full"
          loading="lazy"
        />
      </div>
      <a
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 bg-black/70 hover:bg-black/90 text-white text-xs rounded-full transition-colors"
        data-testid="youtube-external-link"
      >
        <Youtube className="w-3.5 h-3.5" />
        <span>Watch on YouTube</span>
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};

export default YouTubeEmbed;
export { extractYouTubeId };
